<script setup lang="ts">
import './assets/style.scss'
import { computed, type PropType, ref, watch } from 'vue'
import type { ObjectTypeTypeShort } from '@/entities/Manuals/ObjectTypes'
import { useTranslate } from '@/shared/lib'
import { VIcons, VSelectSearch } from '@/shared/ui'

const props = defineProps({
  types: {
    type: Array as PropType<ObjectTypeTypeShort[]>,
    default: () => [],
  },
  section: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  /** Ширина центрального блока (дропдаун + clear) — подгоняется под карточку */
  centerWidth: {
    type: Number,
    default: 0,
  },
})

const { trs } = useTranslate()
const emits = defineEmits(['submit'])

const type = ref(0)

/** Инлайн-ширина центрального блока; дропдаун внутри растягивается флексом */
const centerWidthStyle = computed(() =>
  props.centerWidth > 0 ? { width: `${props.centerWidth}px` } : undefined,
)

const notSetLabel = computed(() => {
  if (props.section === 'passports-config') {
    return trs('itf.not_set_elements')
  }

  return trs('itf.not_set_directory')
})

const directoryLabel = computed(() => {
  if (props.section === 'passports-config') {
    return trs('itf.elements')
  }

  return trs('itf.directory_type')
})

/** Вычисляем данные для списка типов (имена могут содержать ведущие пробелы) **/
const listObjectTypes = computed(() => {
  const safeTypes = Array.isArray(props.types) ? props.types : []
  const sorted = [...safeTypes].sort((a, b) =>
    a.name.trim().localeCompare(b.name.trim(), 'ru'),
  )
  const withIds = sorted.map((item) => ({ ...item, name: item.name.trim() }))
  return [{ id: 0, name: notSetLabel.value }, ...withIds]
})

/**
 * Функция применения фильтра
 */
const submitHandler = () => {
  emits('submit', {
    type: type.value,
  })
}

/* Применяем фильтр сразу при смене выбранного типа */
watch(type, () => submitHandler())

/**
 * Функция очистки фильтра
 */
const clearFilterHandler = () => {
  type.value = 0
}
</script>

<template>
  <div class="invent-types-table__form">
    <div v-if="$slots.prepend" class="invent-types-table__form-prepend">
      <slot name="prepend" />
    </div>

    <div class="invent-types-table__form-center" :style="centerWidthStyle">
      <VSelectSearch
        v-model="type"
        hide-details
        :min-search-length="1"
        :label="directoryLabel"
        :items="listObjectTypes"
        item-title="name"
        item-value="id"
      >
        <template #item="{ item, props: itemProps }">
          <VListItem v-bind="itemProps">
            <template #title>
              <span class="invent-types-table__option-id">
                {{ item.raw.id ? item.raw.id : '' }}
              </span>
              <span>{{ item.raw.name }}</span>
            </template>
          </VListItem>
        </template>
      </VSelectSearch>

      <VBtn
        v-if="type > 0"
        class="invent-types-table__form-clear"
        color="default"
        variant="tonal"
        :aria-label="$t('itf.clear')"
        :title="$t('itf.clear')"
        @click="clearFilterHandler"
      >
        <VIcons type="close" />
      </VBtn>
    </div>

    <div v-if="$slots.append" class="invent-types-table__form-append">
      <slot name="append" />
    </div>
  </div>
</template>
